import React, { useEffect, useState } from "react";
import dashboard from "../../gql/dashboard";
import { useQuery } from "@apollo/client";
import DashboardFilter from "./DashboardFilter";
import Button from "@mui/material/Button";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import { Tooltip } from "@mui/material";
import DashboardCharts from "./DashboardChart";
import { useFormik } from "formik";
import ptpa from "../../gql/ptpa";
import { useTranslation } from "react-i18next";
import DashBoardLoading from "../../common/DashBoardLoading";
import AppButton from "../../shared/AppButton";

function Dashboard(props) {
  const currentDate = new Date();
  const sevenDaysAgo = new Date(currentDate);
  sevenDaysAgo.setDate(currentDate.getDate() - 6);
  const formatInputDate = (date) =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
      date.getDate(),
    ).padStart(2, "0")}`;
  const [showFilter, setShowFilter] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [isFilterLoading, setIsFilterLoading] = useState(false);

  const [fromDate] = useState(formatInputDate(sevenDaysAgo));
  const [toDate] = useState(formatInputDate(currentDate));
  const dashboardFilters = { from_date: fromDate, to_date: toDate };
  const {
    loading: eligibilityLoading,
    data: eligibilityData,
    refetch: refetchEligibility,
  } = useQuery(dashboard.DASHBOARD_ELIGIBILITY, {
    variables: { filters: dashboardFilters },
    fetchPolicy: "no-cache",
  });
  const {
    loading: claimsLoading,
    data: claimsData,
    refetch: refetchClaims,
  } = useQuery(dashboard.DASHBOARD_CLAIMS, {
    variables: { filters: dashboardFilters },
    fetchPolicy: "no-cache",
  });
  const {
    loading: preAuthLoading,
    data: preAuthData,
    refetch: refetchPreAuth,
  } = useQuery(dashboard.DASHBOARD_PRE_AUTH, {
    variables: { filters: dashboardFilters },
    fetchPolicy: "no-cache",
  });
  const loading = eligibilityLoading || claimsLoading || preAuthLoading;
  const hasAnyDashboardData =
    Boolean(eligibilityData?.getDashboardEligibility?.data) ||
    Boolean(claimsData?.getDashboardClaims?.data) ||
    Boolean(preAuthData?.getDashboardPreAuth?.data);

  useEffect(() => {
    const eligibility = eligibilityData?.getDashboardEligibility?.data;
    const claims = claimsData?.getDashboardClaims?.data;
    const preAuth = preAuthData?.getDashboardPreAuth?.data;

    if (eligibility || claims || preAuth) {
      setDashboardData((previousData) => ({
        active_verification:
          eligibility?.active_verification ??
          previousData?.active_verification ??
          0,
        claims_rejection_reasons:
          claims?.claims_rejection_reasons ??
          previousData?.claims_rejection_reasons ??
          [],
        claims_status_breakdown:
          claims?.claims_status_breakdown ??
          previousData?.claims_status_breakdown ??
          {},
        not_active_verification:
          eligibility?.not_active_verification ??
          previousData?.not_active_verification ??
          0,
        patients: eligibility?.patients ?? previousData?.patients ?? 0,
        pre_auth_breakdown:
          preAuth?.pre_auth_breakdown ??
          previousData?.pre_auth_breakdown ??
          [],
        total_claims: claims?.total_claims ?? previousData?.total_claims ?? 0,
        total_pre_auth:
          preAuth?.total_pre_auth ?? previousData?.total_pre_auth ?? 0,
        total_verification:
          eligibility?.total_verification ??
          previousData?.total_verification ??
          0,
        verification_payer_breakdown:
          eligibility?.verification_payer_breakdown ??
          previousData?.verification_payer_breakdown ??
          [],
      }));
    }
  }, [
    eligibilityData?.getDashboardEligibility?.data,
    claimsData?.getDashboardClaims?.data,
    preAuthData?.getDashboardPreAuth?.data,
  ]);

  function formatDate(inputDate) {
    // Create a new Date object from the input date string
    var date = new Date(inputDate);

    // Define months array
    var months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    // Extract the components of the date
    var year = date.getFullYear();
    var month = months[date.getMonth()];
    var day = date.getDate();

    // Format the date
    var formattedDate =
      month + " " + (day < 10 ? "0" + day : day) + ", " + year;

    // Return the formatted date
    return formattedDate;
  }

  const formik = useFormik({
    initialValues: {
      from_date: formatInputDate(sevenDaysAgo),
      to_date: formatInputDate(currentDate),
    },
    onSubmit: async (values, { setSubmitting }) => {
      setIsFilterLoading(true);
      try {
        const from = new Date(`${values.from_date}T00:00:00`);
        const to = new Date(`${values.to_date}T00:00:00`);
        const maximumTo = new Date(from);
        maximumTo.setMonth(maximumTo.getMonth() + 1);
        if (to > maximumTo) {
          formik.setFieldError("to_date", "Date range cannot exceed one month");
          return;
        }
        const filters = Object.keys(values).reduce((acc, key) => {
          const value = values[key];
          if (value !== "" && value !== null) {
            acc[key] = value;
          }
          return acc;
        }, {});
        await Promise.all([
          refetchEligibility({ filters }),
          refetchClaims({ filters }),
          refetchPreAuth({ filters }),
        ]);
      } finally {
        setIsFilterLoading(false);
        setSubmitting(false);
      }
    },
  });

  const { data: payersData } = useQuery(ptpa.GET_ALL_PAYERS);

  React.useEffect(() => {
    if (payersData?.listPayersWithTpa?.data) {
      localStorage.setItem(
        "payersData",
        JSON.stringify(payersData.listPayersWithTpa.data),
      );
    }
  }, [payersData]);

  const { t } = useTranslation();

  return (
    <React.Fragment>
      <div className="row">
        <div className="flex w-full justify-end mb-2">
          {/* <div className="flex items-center gap-2">
            <div className="text-base  text-gray-600 font-semibold">
              <span className="font-medium">{t("Filter By")}: </span>{" "}
              {formatDate(formik.values.from_date)} -{" "}
              {formatDate(formik.values.to_date)}
            </div>
          </div> */}
          {/* <AppButton
            variant="outlined"
            label={t("Filter Toggle")}
            startIcon={<FilterAltOutlinedIcon className="text-[13px]" />}
            onClick={() => setShowFilter(() => !showFilter)}
            className=" !border-primary !text-primary hover:!bg-primary/10"
          /> */}
        </div>
        {showFilter && (
          <div>
            <DashboardFilter
              formik={formik}
              showFilter={showFilter}
              setShowFilter={setShowFilter}
              isLoading={loading || isFilterLoading}
            />
          </div>
        )}
        <div className="">
          {loading && !hasAnyDashboardData && !dashboardData ? (
            <div className="">
              <DashBoardLoading />
            </div>
          ) : (
            dashboardData && (
              <div className="opacity-100">
                <DashboardCharts data={dashboardData} />
              </div>
            )
          )}
        </div>
      </div>
    </React.Fragment>
  );
}

export default Dashboard;
